import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import EmailLink from './EmailLink';
import { InvoiceViewModal } from './InvoiceViewModal';
import PhoneLink from './PhoneLink';
import { useAuth } from '../context/AuthContext';
import { floorService, invoiceService, roomService, tenantService } from '../services/api';
import { DailyGuest, Floor, Invoice, Room, User } from '../types';
import { paymentTypeLabel } from '../utils/paymentDisplay';
import {
  formatGuestMoney,
  guestInvoicePeriodLabel,
  guestInvoiceSummary,
  isGuestStayInvoice,
} from '../utils/guestInvoiceDisplay';
import { guestBalanceDue, guestStayDays } from '../utils/dailyGuestDates';
import { formatLocationByIds } from '../utils/locationLabels';
import { resolveMediaUrl } from '../utils/mediaUrl';
import '../pages/ViewTenantUser.css';
import './GuestViewModal.css';

export type GuestViewModalProps = {
  guest: DailyGuest | null;
  hostelId: number;
  onClose: () => void;
  onPay: () => void;
  onEdit: () => void;
  onCheckout: () => void;
  onConvertToTenant?: () => void;
};

function formatDate(v: string | undefined | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function proofUrlKind(url: string): 'image' | 'pdf' | 'other' {
  const path = url.split(/[?#]/)[0]?.toLowerCase() ?? '';
  if (/\.(jpe?g|png|gif|webp|bmp|svg)$/.test(path)) return 'image';
  if (/\.pdf$/i.test(path)) return 'pdf';
  return 'other';
}

const GuestViewModal: React.FC<GuestViewModalProps> = ({
  guest,
  hostelId,
  onClose,
  onPay,
  onEdit,
  onCheckout,
  onConvertToTenant,
}) => {
  const { hostel: authHostel } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [idProofPreview, setIdProofPreview] = useState<{ url: string; label: string } | null>(null);
  const [invoiceView, setInvoiceView] = useState<Invoice | null>(null);

  const userId = guest?.userId != null ? Number(guest.userId) : NaN;

  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const ta = new Date(a.paymentTimestamp || 0).getTime();
      const tb = new Date(b.paymentTimestamp || 0).getTime();
      return tb - ta;
    });
  }, [invoices]);

  useEffect(() => {
    if (!guest || !Number.isFinite(userId)) {
      setProfile(null);
      setInvoices([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [u, fl, rm, inv] = await Promise.all([
          tenantService.getHostelTenant(hostelId, userId),
          floorService.getFloorsByHostel(hostelId),
          roomService.getRoomsByHostel(hostelId),
          invoiceService.getUserInvoices(userId).catch(() => [] as Invoice[]),
        ]);
        if (!cancelled) {
          setProfile(u);
          setFloors(fl.filter((f) => f.isActive !== false));
          setRooms(rm.filter((r) => r.isActive !== false));
          setInvoices(Array.isArray(inv) ? inv : []);
        }
      } catch {
        if (!cancelled) {
          setProfile(null);
          setInvoices([]);
          toast.error('Could not load guest profile');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [guest?.id, guest?.amountPaid, hostelId, userId]);

  const handleDownloadInvoicePdf = async (inv: Invoice) => {
    try {
      const blob = await invoiceService.downloadInvoice(inv.id, inv.userId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${inv.invoiceNumber || inv.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice PDF downloaded');
    } catch {
      toast.error('Failed to download invoice PDF');
    }
  };

  const invoiceDateDisplay = (v: string | undefined | null) => {
    if (!v) return '—';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const floorById = useMemo(
    () => Object.fromEntries(floors.map((f) => [f.id, f])) as Record<number, Floor>,
    [floors],
  );
  const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])) as Record<number, Room>, [rooms]);

  const govIdItems = useMemo(() => {
    if (!profile) return [];
    const items: { key: string; label: string; url: string }[] = [];
    const front = profile.idFrontSide ? resolveMediaUrl(profile.idFrontSide) || profile.idFrontSide : '';
    const back = profile.idBackSide ? resolveMediaUrl(profile.idBackSide) || profile.idBackSide : '';
    if (front) items.push({ key: 'front', label: 'ID front', url: front });
    if (back) items.push({ key: 'back', label: 'ID back', url: back });
    return items;
  }, [profile]);

  if (!guest) return null;

  const days = guestStayDays(guest.checkInDate, guest.checkOutDate);
  const rate = Number(guest.dailyRate) || 0;
  const total = Math.round(days * rate * 100) / 100;
  const paid = Number(guest.amountPaid) || 0;
  const balance = guestBalanceDue(guest);
  const isActive = String(guest.status || '').toUpperCase() === 'ACTIVE';
  const hasLogin = Number.isFinite(userId);

  const stayLocation = formatLocationByIds(guest.floorId ?? null, guest.roomId ?? null, {
    floorById,
    roomById,
  });

  const photoUrl = profile?.photo ? resolveMediaUrl(profile.photo) || profile.photo : null;
  const kycDone = Boolean(profile?.idFrontSide && profile?.idBackSide);

  return (
    <div className="guest-view-backdrop" role="presentation" onClick={onClose}>
      <div
        className="guest-view-modal guest-view-modal--profile"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-view-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="guest-view-modal__toolbar">
          <h2 id="guest-view-title">Guest profile</h2>
          <button type="button" className="guest-view-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="guest-view-modal__scroll">
          {loading && hasLogin ? (
            <p className="guest-view-loading">Loading profile…</p>
          ) : (
            <div className="vtv-layout guest-view-vtv">
              <aside className="vtv-sidebar">
                <div className="vtv-card vtv-profile-card">
                  <div className="vtv-avatar-wrap">
                    {photoUrl ? (
                      <img src={photoUrl} alt="" className="vtv-avatar" />
                    ) : (
                      <div className="vtv-avatar vtv-avatar--placeholder" aria-hidden>
                        {(profile?.username || guest.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <h3 className="vtv-profile-name">{profile?.username || guest.name}</h3>
                  <p className={`vtv-status ${isActive ? 'vtv-status--active' : 'vtv-status--inactive'}`}>
                    {isActive ? 'Active stay' : guest.status || '—'}
                  </p>
                  <dl className="vtv-quick-dl">
                    <div>
                      <dt>Phone</dt>
                      <dd>
                        <PhoneLink phone={guest.phoneNumber} className="vtv-link" />
                      </dd>
                    </div>
                    {profile?.email ? (
                      <div>
                        <dt>Email</dt>
                        <dd>
                          <EmailLink email={profile.email} className="vtv-link" />
                        </dd>
                      </div>
                    ) : null}
                    <div>
                      <dt>Location</dt>
                      <dd>{stayLocation || '—'}</dd>
                    </div>
                  </dl>
                  <div className="vtv-tags">
                    <span className="vtv-tag">Daily guest</span>
                    {hasLogin ? (
                      <span className="vtv-tag vtv-tag--ok">Portal login</span>
                    ) : (
                      <span className="vtv-tag vtv-tag--warn">No login</span>
                    )}
                    {kycDone ? (
                      <span className="vtv-tag vtv-tag--ok">KYC complete</span>
                    ) : (
                      <span className="vtv-tag vtv-tag--warn">KYC pending</span>
                    )}
                  </div>
                </div>

                {govIdItems.length > 0 ? (
                  <div className="vtv-card vtv-id-docs-card">
                    <h3 className="vtv-id-docs-title">Government ID</h3>
                    <div className="vtv-id-docs-grid">
                      {govIdItems.map((item) => {
                        const kind = proofUrlKind(item.url);
                        return (
                          <div key={item.key} className="vtv-id-doc-cell">
                            <span className="vtv-id-doc-label">{item.label}</span>
                            {kind === 'image' ? (
                              <button
                                type="button"
                                className="vtv-id-doc-media-btn vtv-id-doc-media-btn--image"
                                onClick={() => setIdProofPreview({ url: item.url, label: item.label })}
                              >
                                <img src={item.url} alt="" className="vtv-id-doc-image" loading="lazy" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={`vtv-id-doc-media-btn vtv-id-doc-media-btn--${kind === 'pdf' ? 'pdf' : 'file'}`}
                                onClick={() => setIdProofPreview({ url: item.url, label: item.label })}
                              >
                                <span className="vtv-id-doc-badge">{kind === 'pdf' ? 'PDF' : 'File'}</span>
                                <span className="vtv-id-doc-hint">Click to preview</span>
                              </button>
                            )}
                            <a
                              className="vtv-id-doc-download"
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : !hasLogin ? (
                  <p className="guest-view-kyc-hint">
                    Photo and ID proofs can be added when creating the guest (requires default password in Settings).
                  </p>
                ) : (
                  <p className="guest-view-kyc-hint">No government ID uploaded yet.</p>
                )}
              </aside>

              <main className="vtv-main">
                <section className="vtv-card vtv-section-card">
                  <h3 className="vtv-section-heading">Personal details</h3>
                  <div className="vtv-field-grid">
                    <div className="vtv-field">
                      <span className="vtv-field-label">Full name</span>
                      <span className="vtv-field-value">{profile?.username || guest.name}</span>
                    </div>
                    <div className="vtv-field">
                      <span className="vtv-field-label">Phone</span>
                      <span className="vtv-field-value">
                        <PhoneLink phone={guest.phoneNumber} className="vtv-link" />
                      </span>
                    </div>
                    <div className="vtv-field">
                      <span className="vtv-field-label">Email</span>
                      <span className="vtv-field-value">
                        <EmailLink email={profile?.email} className="vtv-link" />
                      </span>
                    </div>
                    <div className="vtv-field vtv-field--full">
                      <span className="vtv-field-label">Address</span>
                      <span className="vtv-field-value">{profile?.address?.trim() || '—'}</span>
                    </div>
                  </div>
                </section>

                <section className="vtv-card vtv-section-card">
                  <h3 className="vtv-section-heading">Stay &amp; payment</h3>
                  <div className="vtv-field-grid">
                    <div className="vtv-field">
                      <span className="vtv-field-label">Check-in</span>
                      <span className="vtv-field-value">{formatDate(guest.checkInDate)}</span>
                    </div>
                    <div className="vtv-field">
                      <span className="vtv-field-label">Check-out</span>
                      <span className="vtv-field-value">{formatDate(guest.checkOutDate)}</span>
                    </div>
                    <div className="vtv-field">
                      <span className="vtv-field-label">Daily rate</span>
                      <span className="vtv-field-value">₹{rate.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="vtv-field">
                      <span className="vtv-field-label">Stay total</span>
                      <span className="vtv-field-value">₹{total.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="vtv-field">
                      <span className="vtv-field-label">Paid</span>
                      <span className="vtv-field-value">₹{paid.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="vtv-field">
                      <span className="vtv-field-label">Balance due</span>
                      <span className={`vtv-field-value${balance > 0 ? ' guest-view-due' : ''}`}>
                        ₹{balance.toLocaleString('en-IN')}
                      </span>
                    </div>
                    {guest.comments ? (
                      <div className="vtv-field vtv-field--full">
                        <span className="vtv-field-label">Comments</span>
                        <span className="vtv-field-value">{guest.comments}</span>
                      </div>
                    ) : null}
                  </div>
                </section>

                <section className="vtv-card vtv-section-card">
                  <div className="vtv-section-head-row">
                    <h3 className="vtv-section-heading vtv-section-heading--flush">Transactions</h3>
                    <span className="vtv-section-hint">Invoices for this guest in this hostel</span>
                  </div>
                  {!hasLogin ? (
                    <p className="vtv-muted vtv-muted--block">
                      No portal login linked — invoices appear after the guest has a login account and payments are
                      recorded.
                    </p>
                  ) : sortedInvoices.length === 0 ? (
                    <p className="vtv-muted vtv-muted--block">No invoices yet. Record a payment to generate one.</p>
                  ) : (
                    <div className="vtv-table-wrap responsive-table-wrap">
                      <table className="vtv-transactions-table responsive-table">
                        <thead>
                          <tr>
                            <th>Invoice</th>
                            <th>Stay charges</th>
                            <th>Type</th>
                            <th>Total</th>
                            <th>Paid</th>
                            <th>Balance</th>
                            <th>Date</th>
                            <th className="vtv-th-actions">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedInvoices.map((inv) => {
                            const guestInv = isGuestStayInvoice(inv);
                            const period = guestInvoicePeriodLabel(inv, guest);
                            const sum = guestInv ? guestInvoiceSummary(inv, guest) : null;
                            return (
                            <tr key={inv.id}>
                              <td className="vtv-mono responsive-table__primary" data-label="Invoice">
                                #{inv.invoiceNumber || inv.id}
                              </td>
                              <td data-label="Stay charges">
                                <span className="guest-inv-charges-line">{period.line1}</span>
                                {period.line2 ? (
                                  <span className="guest-inv-charges-sub">{period.line2}</span>
                                ) : null}
                              </td>
                              <td data-label="Type">{paymentTypeLabel(inv.paymentType)}</td>
                              <td data-label="Total">
                                {sum ? formatGuestMoney(sum.totalDue) : '—'}
                              </td>
                              <td data-label="Paid">
                                {sum ? formatGuestMoney(sum.paid) : inv.paidAmount ?? '—'}
                              </td>
                              <td data-label="Balance">
                                {sum ? formatGuestMoney(sum.balance) : inv.balanceAmount ?? '—'}
                              </td>
                              <td data-label="Date">{invoiceDateDisplay(inv.paymentTimestamp)}</td>
                              <td className="responsive-table__actions" data-label="Actions">
                                <div className="vtv-row-actions">
                                  <button type="button" className="vtv-mini-btn" onClick={() => setInvoiceView(inv)}>
                                    View
                                  </button>
                                  <button
                                    type="button"
                                    className="vtv-mini-btn vtv-mini-btn--primary"
                                    onClick={() => void handleDownloadInvoicePdf(inv)}
                                  >
                                    PDF
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              </main>
            </div>
          )}
        </div>

        <div className="guest-view-modal__foot">
          <button type="button" className="guest-view-foot-btn guest-view-foot-btn--pay" onClick={onPay}>
            Pay
          </button>
          <button type="button" className="guest-view-foot-btn guest-view-foot-btn--edit" onClick={onEdit}>
            Edit
          </button>
          {isActive && onConvertToTenant ? (
            <button
              type="button"
              className="guest-view-foot-btn guest-view-foot-btn--convert"
              onClick={onConvertToTenant}
            >
              Convert to tenant
            </button>
          ) : null}
          {isActive ? (
            <button type="button" className="guest-view-foot-btn guest-view-foot-btn--out" onClick={onCheckout}>
              Check out
            </button>
          ) : null}
        </div>
      </div>

      {invoiceView ? (
        <InvoiceViewModal
          invoice={invoiceView}
          tenant={profile}
          hostel={authHostel}
          locationLookup={{ floorById, roomById }}
          guestStay={guest}
          onClose={() => setInvoiceView(null)}
          onDownload={(inv) => void handleDownloadInvoicePdf(inv)}
        />
      ) : null}

      {idProofPreview ? (
        <div
          className="guest-view-id-preview"
          role="presentation"
          onClick={() => setIdProofPreview(null)}
        >
          <div className="guest-view-id-preview__inner" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="guest-view-id-preview__close" onClick={() => setIdProofPreview(null)}>
              ×
            </button>
            <h4>{idProofPreview.label}</h4>
            {proofUrlKind(idProofPreview.url) === 'image' ? (
              <img src={idProofPreview.url} alt={idProofPreview.label} />
            ) : (
              <iframe title={idProofPreview.label} src={idProofPreview.url} className="guest-view-id-preview__frame" />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GuestViewModal;
